import { Component, inject, Input, OnChanges } from '@angular/core';
import { MyCompetitionBrandPerformanceByTime, MyCompetitionPerformanceByTimeWidgetResponse, PerformanceByTimeColors } from '../types';
import { SelectItem } from '../../../shared/components/select-menu/select-menu.component';
import { PerformanceComparisonMetric } from '../../my-brand-screen/types';
import { CommonModule } from '@angular/common';
import { UtilService } from '../../../services/util.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TippyDirective } from '@ngneat/helipopper';
import { HourTwoDigitsPipe } from '../../../pipes/hour-two-digits.pipe';

@Component({
  selector: 'app-performance-by-time',
  imports: [
    CommonModule,
    LoaderComponent,
    NoWidgetDataComponent,
    MatTooltip,
    TippyDirective,
    HourTwoDigitsPipe
  ],
  templateUrl: './performance-by-time.component.html',
  styleUrls: ['./performance-by-time.component.scss']
})
export class PerformanceByTimeComponent implements OnChanges {
  @Input() loading = false;
  @Input() data?: MyCompetitionPerformanceByTimeWidgetResponse;
  @Input() metric?: SelectItem;
  @Input() brandColors?: Record<string, string>;

  utilService = inject(UtilService);

  colors = PerformanceByTimeColors;
  noDataColor: string = '#BDC2CA';
  steps: string[] = [];
  minValue: number = 0;
  maxValue: number = 100;
  brandValueColorMap: { [brandKey: string]: { value: number | null; color: string }[] } = {};

  ngOnChanges(): void {
    if (this.data?.brands?.length && this.metric?.key) {
      this.setValuesRange(this.data.brands, this.metric.key as PerformanceComparisonMetric);
      this.setSteps(this.metric.key as PerformanceComparisonMetric);
      this.setBrandValueColorMap(this.data.brands, this.metric.key as PerformanceComparisonMetric);
    }
  }

  private setValuesRange(brands: MyCompetitionBrandPerformanceByTime[], metric: PerformanceComparisonMetric): void {
    //@ts-ignore
    const values = brands.flatMap(brand => brand[metric] ?? []);
    const { min, max } = this.utilService.getMetricRange(values);
    this.minValue = min;
    this.maxValue = max;
  }

  private setSteps(metric: PerformanceComparisonMetric): void {
    const steps = [];
    const stepCount = this.colors.length;
    const range = this.maxValue - this.minValue;

    for (let i = 0; i < stepCount; i++) {
      const value = this.minValue + (range / (stepCount - 1)) * i;
      const formattedValue = this.utilService.getDisplayMetric(value, metric, true);
      steps.push(formattedValue);
    }

    this.steps = steps;
  }

  private setBrandValueColorMap(brands: MyCompetitionBrandPerformanceByTime[], metric: PerformanceComparisonMetric): void {
    const map: { [brandKey: string]: { value: number | null; color: string }[] } = {};

    brands.forEach(brand => {
      const values = this.getValuesForBrand(brand);
      map[brand.brandKey] = values.map(value => ({
        value: value,
        color: this.getColorForValue(value)
      }));
    });

    this.brandValueColorMap = map;
  }

  private getValuesForBrand(brand: any): number[] {
    const key = this.metric?.key as PerformanceComparisonMetric;
    return (brand[key] as number[]) ?? [];
  }

  private getColorForValue(value: number | null): string {
    if (!(typeof value === 'number')) {
      return this.noDataColor;
    }

    const ratio = (value - this.minValue) / (this.maxValue - this.minValue);
    const scaled = ratio * (this.colors.length - 1);

    const idx = Math.floor(scaled);
    const t = scaled - idx;

    const startColor = this.utilService.hexToRgb(this.colors[idx]);
    const endColor = this.utilService.hexToRgb(this.colors[Math.min(idx + 1, this.colors.length - 1)]);

    const r = Math.round(startColor.r + t * (endColor.r - startColor.r));
    const g = Math.round(startColor.g + t * (endColor.g - startColor.g));
    const b = Math.round(startColor.b + t * (endColor.b - startColor.b));

    return `rgb(${r}, ${g}, ${b})`;
  }

  getTooltipForValue(value: number | null, brand: MyCompetitionBrandPerformanceByTime, hourIndex: number): { brandName: string; brandColor?: string; hour: string; value: string } {
    return {
      brandName: brand.brandName,
      brandColor: this.brandColors ? this.brandColors[brand.brandKey] : undefined,
      hour: `${hourIndex}:00`,
      value: (typeof value === 'number') ? this.utilService.getDisplayMetric(value, this.metric?.key, false) : 'No data',
    }
  }
}
